import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from "../config"

const AdminLogin = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiStatus, setApiStatus] = useState('');
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("adminToken");

      if (token) {
        try {
          // Try multiple endpoints for token verification
          const endpoints = [
            `${API_BASE}/test-auth`,
            '/api/v2/admin/test-auth',
            '/api/admin/verify-token.php'
          ];
          
          let response = null;
          for (const endpoint of endpoints) {
            try {
              console.log(`Testing token endpoint: ${endpoint}`);
              response = await fetch(endpoint, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (response.ok) break;
            } catch (err) {
              continue;
            }
          }
          
          if (response && response.ok) {
            const data = await response.json();
            if (data.success) {
              navigate("/admin/dashboard");
            } else {
              localStorage.removeItem("adminToken");
            }
          } else {
            localStorage.removeItem("adminToken");
          }
        } catch (err) {
          console.error("Token verification error:", err);
          localStorage.removeItem("adminToken");
        }
      }
    };

    verifyToken();
  }, [navigate]);

  // Test API on component mount
  useEffect(() => {
    const testAPI = async () => {
      try {
        const testURL = `${API_BASE}/test`;
        console.log(`Testing API at: ${testURL}`);
        const response = await fetch(testURL);
        if (response.ok) {
          const data = await response.json();
          setApiStatus(`API Connected: ${data.message}`);
        } else {
          setApiStatus(`API Error: ${response.status} ${response.statusText}`);
        }
      } catch (err) {
        setApiStatus(`API Error: ${err.message}`);
      }
    };
    
    testAPI();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Try multiple possible endpoints
      const endpoints = [
        `${API_BASE}/login`,
        '/api/v2/admin/login',
        '/api/admin/login',
        '/api/admin-login.php'
      ];
      
      console.log('Trying login endpoints:');
      endpoints.forEach(url => console.log(`  - ${url}`));
      
      let response = null;
      let successfulEndpoint = '';
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Attempting login at: ${endpoint}`);
          console.log(`Payload:`, { email: formData.email, password: formData.password ? '***' : '' });
          
          response = await fetch(endpoint, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              email: formData.email,
              password: formData.password,
            }),
          });
          
          console.log(`Response from ${endpoint}:`, response.status, response.statusText);
          
          if (response.ok) {
            successfulEndpoint = endpoint;
            break;
          }
        } catch (err) {
          console.log(`Failed at ${endpoint}:`, err.message);
          continue;
        }
      }
      
      if (!response) {
        throw new Error('All login endpoints failed');
      }
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Server response:', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Login successful at:', successfulEndpoint);
      console.log('Login response data:', data);

      if (data.success) {
        console.log('✅ Login successful!');
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminData', JSON.stringify(data.admin));
        
        alert('Login successful! Redirecting to dashboard...');
        navigate('/admin/dashboard');
      } else {
        setError(data.error || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      console.error('🔥 Login error:', err);
      setError(`Login failed: ${err.message}. Please check console for details.`);
    } finally {
      setLoading(false);
    }
  };

  const useTestCredentials = () => {
    setFormData({
      email: 'admin@example.com',
      password: 'admin123'
    });
  };

  const testAPIConnection = async () => {
    try {
      const endpoints = [
        `${API_BASE}/test`,
        '/api/v2/admin/test',
        '/api/v2/admin/index.php?endpoint=test',
        '/api/admin/test'
      ];
      
      let result = null;
      
      for (const endpoint of endpoints) {
        try {
          console.log(`Testing API at: ${endpoint}`);
          const response = await fetch(endpoint);
          if (response.ok) {
            const data = await response.json();
            result = { endpoint, data };
            break;
          }
        } catch (err) {
          continue;
        }
      }
      
      if (result) {
        alert(`✅ API Connected!\nEndpoint: ${result.endpoint}\nMessage: ${result.data.message}`);
        setApiStatus(`✅ Connected to: ${result.endpoint}`);
      } else {
        alert('❌ All API endpoints failed');
        setApiStatus('❌ No API connection');
      }
    } catch (err) {
      alert(`❌ API Test Error: ${err.message}`);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Admin Dashboard Login
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Saints Academy Management System
          </p>
          <div className="mt-2 text-center">
            <p className="text-xs font-medium text-blue-600">API Base: {API_BASE}</p>
            <p className="text-xs text-green-600 mt-1">{apiStatus}</p>
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">Email address</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Email address"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Password</label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Password"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </>
              ) : 'Sign in'}
            </button>
            
            <button
              type="button"
              onClick={useTestCredentials}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
            >
              Use Test Credentials
            </button>
            
            <button
              type="button"
              onClick={testAPIConnection}
              className="w-full flex justify-center py-2 px-4 border border-yellow-300 text-sm font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors"
            >
              Test API Connection
            </button>
          </div>
        </form>

        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-blue-800 mb-2">Test Credentials:</h3>
          <div className="text-xs text-blue-700 space-y-1">
            <p><span className="font-semibold">Email:</span> admin@example.com</p>
            <p><span className="font-semibold">Password:</span> admin123</p>
          </div>
          <div className="mt-3 text-xs text-blue-600">
            <p className="font-semibold">API Endpoints being tried:</p>
            <ul className="mt-1 space-y-1">
              <li>• {API_BASE}/login</li>
              <li>• /api/v2/admin/login</li>
              <li>• /api/admin/login</li>
              <li>• /api/admin-login.php</li>
            </ul>
          </div>
        </div>

        <div className="text-center text-xs text-gray-500 mt-4">
          <p>This is a protected admin area. Unauthorized access is prohibited.</p>
          <p className="mt-1">Check browser console for detailed debugging information.</p>
          <div className="mt-2 p-2 bg-gray-50 rounded border">
            <p className="font-medium">Debug Info:</p>
            <p>Current API_BASE: {API_BASE}</p>
            <p>Email: {formData.email || 'Not entered'}</p>
            <p>Password: {formData.password ? 'Entered' : 'Not entered'}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;